import React, { useState, useRef } from 'react';

import { getIconByName } from '../../../../../reusable/IconLibrary/IconLibrary';

import { WidgetProps } from './WidgetProps';
import { getBorderStyle, getDropShadowStyle } from './helper';
import { useWidgetAnimation } from './useWidgetAnimation';

type GenerateIconStyleProps = {
	isShowWidget?: boolean;
	isExiting?: boolean;
	isHover: boolean;
	isClickable?: boolean;
	style: {
		textLabel: {
			color: string;
			hoverColor: string;
		};
		backgroundSettings: WidgetProps.StyleProps['backgroundSettings'];
		transformSettings: WidgetProps.StyleProps['transformSettings'];
	};
};
const generateIconStyle = ({ isShowWidget, isExiting, style, isHover, isClickable }: GenerateIconStyleProps) => {
	const {
		textLabel: { color, hoverColor },
	} = style;

	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);

	return {
		display: isShowWidget || isExiting ? 'inline-block' : 'none',
		color: isHover ? (hoverColor || color) : color,
		width: '100%',
		height: '100%',
		border: isHover ? (hoverBorder || border) : border,
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
	};
};

const IconWidget = ({
	id,
	isShowWidget,
	isExiting,
	onExitDone,
	itemRef,
	onClickItem,
	widgetTemplateId,
	style,
	isSafari,
	settings,
	onContextMenu,
	isClickable,
}: WidgetProps.BaseProps) => {
	const { iconKey } = settings;

	const gElementRef = useRef<SVGGElement | null>();
	// the icon <svg> itself - its `fill` flashes to the active colour on press
	const bgElementRef = useRef<SVGSVGElement | null>();

	const [isHover, setIsHover] = useState(false);

	const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, isClickable });

	const handleMouseEnter = () => {
		setIsHover(true);
	};

	const handleMouseLeave = () => {
		setIsHover(false);
	};

	return (
		<g id={widgetTemplateId} className={`${!isSafari ? 'widget' : ''} widget_${widgetTemplateId}`}>
			<g ref={itemRef}>
				<g
					ref={r => gElementRef.current = r}
					data-widget-id={id}
					data-template-id={widgetTemplateId}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onContextMenu={(e) => {
						e.preventDefault();
						onContextMenu?.(itemRef, !!isShowWidget);
						return false;
					}}
				>
					<foreignObject>
						{getIconByName(iconKey, {
							ref: (r: SVGSVGElement | null) => (bgElementRef.current = r),
							style: generateIconStyle({ isShowWidget, isExiting, style, isHover, isClickable }),
							onClick: (e: any) => {
								e.stopPropagation();
								triggerClickAnimation(() => onClickItem?.(itemRef));
							},
						})}
					</foreignObject>
				</g>
			</g>
		</g>
	);
};

export default IconWidget;
